/*
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：メニューコンフィグレーション
 *
 * 作成者：2021/11/24 ノア）虞
 * 最終修正者：2021/11/29 ノア）虞
 *
 */
import { PageType, UserRole } from '@/commons/constants'
import { MenuItem } from '@/models/home'
import { i18n } from '@/locales'
import { localService } from '@/commons/services/localService'

/**
 * コンフィグレーション
 *
 * @returns {Record<string, MenuItem[]>} コンフィグレーション
 */
// eslint-disable-next-line max-lines-per-function
export function getMenuConfig(): Record<string, MenuItem[]> {
  return {
    menuList: [
      {
        type: PageType.PAGE,
        menuId: 'dynamicMap',
        menuName: i18n.t('menu.dynamicMap') as string,
        icon: 'wo_002',
        role: UserRole.USER,
        children: []
      },
      {
        type: PageType.PAGE,
        menuId: 'managementList',
        menuName: i18n.t('menu.managementList', [localService.userInfo?.managementTargetShowName]) as string,
        icon: 'wo_004',
        role: UserRole.USER,
        children: []
      },
      {
        type: PageType.NOPAGE,
        menuId: 'migrationResult',
        menuName: i18n.t('menu.migrationResult') as string,
        icon: 'wo_006',
        children: [
          {
            type: PageType.PAGE,
            menuId: 'historyManagement',
            menuName: i18n.t('menu.historyManagement') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'analysisByBusinessOffices',
            menuName: i18n.t('menu.analysisByBusinessOffices') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'areaAnalysis',
            menuName: i18n.t('menu.areaAnalysis') as string,
            role: UserRole.USER
          }
        ]
      },
      {
        type: PageType.NOPAGE,
        menuId: 'master',
        menuName: 'マスタ',
        icon: 'wo_007',
        children: [
          {
            type: PageType.PAGE,
            menuId: 'subjectMaster',
            menuName: i18n.t('menu.subjectMaster', [localService.userInfo?.managementTargetShowName]) as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'subjectMasterAllocation',
            menuName: i18n.t('menu.subjectMasterAllocation', [
              localService.userInfo?.managementTargetShowName
            ]) as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'headMaster',
            menuName: i18n.t('menu.headMaster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'gpsTrackeraster',
            menuName: i18n.t('menu.gpsTrackeraster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'managementLocationMaster',
            menuName: i18n.t('menu.managementLocationMaster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'managementLocationDetailMaster',
            menuName: i18n.t('menu.managementLocationDetailMaster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'businessOfficeMaster',
            menuName: i18n.t('menu.businessOfficeMaster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'businessOfficeAllocation',
            menuName: i18n.t('menu.businessOfficeAllocation') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'userMaster',
            menuName: i18n.t('menu.userMaster') as string,
            role: UserRole.SYSTEM
          },
          {
            type: PageType.PAGE,
            menuId: 'areaMaster',
            menuName: i18n.t('menu.areaMaster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'areaAllocation',
            menuName: i18n.t('menu.prefAllocMaster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'gpsTrackerTerminalConfig',
            menuName: i18n.t('menu.gpsTrackerTerminalConfig') as string,
            role: UserRole.SYSTEM
          },
          {
            type: PageType.PAGE,
            menuId: 'corporateManagement',
            menuName: i18n.t('menu.corporateManagement') as string,
            role: UserRole.SYSTEM
          },
          {
            type: PageType.PAGE,
            menuId: 'gpsTrackerExchange',
            menuName: i18n.t('menu.gpsTrackerExchangeLeft') as string,
            role: UserRole.SYSTEM
          }
        ]
      },
      {
        type: PageType.PAGE,
        menuId: 'alertList',
        menuName: i18n.t('menu.alertList') as string,
        role: UserRole.SYSTEM,
        icon: 'wo_032',
        children: []
      }
    ],

    screenList: [
      {
        type: PageType.NOPAGE,
        menuName: i18n.t('menu.map') as string,
        icon: 'wo_002',
        children: [
          {
            type: PageType.PAGE,
            menuId: 'dynamicMap',
            menuName: i18n.t('menu.dynamicMap') as string,
            role: UserRole.USER
          }
        ]
      },
      {
        type: PageType.NOPAGE,
        menuName: i18n.t('menu.managementList', [localService.userInfo?.managementTargetShowName]) as string,
        icon: 'wo_004',
        children: [
          {
            type: PageType.PAGE,
            menuId: 'managementList',
            menuName: i18n.t('menu.managementList', [localService.userInfo?.managementTargetShowName]) as string,
            role: UserRole.USER
          }
        ]
      },
      {
        type: PageType.NOPAGE,
        menuName: i18n.t('menu.migrationResult') as string,
        icon: 'wo_006',
        children: [
          {
            type: PageType.PAGE,
            menuId: 'historyManagement',
            menuName: i18n.t('menu.historyManagement') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'analysisByBusinessOffices',
            menuName: i18n.t('menu.analysisByBusinessOffices') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'areaAnalysis',
            menuName: i18n.t('menu.areaAnalysis') as string,
            role: UserRole.USER
          }
        ]
      },
      {
        type: PageType.NOPAGE,
        menuName: i18n.t('menu.master') as string,
        icon: 'wo_007',
        children: [
          {
            type: PageType.PAGE,
            menuId: 'subjectMaster',
            menuName: i18n.t('menu.subjectMaster', [localService.userInfo?.managementTargetShowName]) as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'subjectMasterAllocation',
            menuName: i18n.t('menu.subjectMasterAllocation', [
              localService.userInfo?.managementTargetShowName
            ]) as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'headMaster',
            menuName: i18n.t('menu.headMaster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'gpsTrackeraster',
            menuName: i18n.t('menu.gpsTrackeraster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'managementLocationMaster',
            menuName: i18n.t('menu.managementLocationMaster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'managementLocationDetailMaster',
            menuName: i18n.t('menu.managementLocationDetailMaster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'businessOfficeMaster',
            menuName: i18n.t('menu.businessOfficeMaster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'businessOfficeAllocation',
            menuName: i18n.t('menu.businessOfficeAllocation') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'userMaster',
            menuName: i18n.t('menu.userMaster') as string,
            role: UserRole.SYSTEM
          },
          {
            type: PageType.PAGE,
            menuId: 'areaMaster',
            menuName: i18n.t('menu.areaMaster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'areaAllocation',
            menuName: i18n.t('menu.prefAllocMaster') as string,
            role: UserRole.USER
          },
          {
            type: PageType.PAGE,
            menuId: 'gpsTrackerTerminalConfig',
            menuName: i18n.t('menu.gpsTrackerTerminalConfig') as string,
            role: UserRole.SYSTEM
          },
          {
            type: PageType.PAGE,
            menuId: 'corporateManagement',
            menuName: i18n.t('menu.corporateManagement') as string,
            role: UserRole.SYSTEM
          },
          {
            type: PageType.PAGE,
            menuId: 'gpsTrackerExchange',
            menuName: i18n.t('menu.gpsTrackerExchange') as string,
            role: UserRole.SYSTEM
          }
        ]
      },
      {
        type: PageType.NOPAGE,
        menuName: i18n.t('menu.alertList') as string,
        icon: 'wo_032',
        children: [
          {
            type: PageType.PAGE,
            menuId: 'alertList',
            menuName: i18n.t('menu.alertList') as string,
            role: UserRole.SYSTEM
          }
        ]
      }
    ]
  }
}
